import { useRef, useEffect } from "react";

export const usePrevious = (val) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = val;
    }, [val]);
    return ref.current;
};

export const getPoints = ({
    pointActiveSize,
    height,
    size
}) => {
    const halfSize = pointActiveSize / 2;
    const sizePerItem = height / size;
    const halfSizePerItem = sizePerItem / 2;
    return Array.from({ length: size ** 2 }).map((x, i) => ({
        x: ((sizePerItem * (i % size)) + halfSizePerItem) - halfSize,
        y: ((sizePerItem * Math.floor(i / size)) + halfSizePerItem) - halfSize
    }));
};

export const getDistance = (p1, p2) => Math.sqrt(((p2.x - p1.x) ** 2) + ((p2.y - p1.y) ** 2));
export const getAngle = (p1, p2) => Math.atan2(p2.y - p1.y, p2.x - p1.x);

export const getCollidedPointIndex = (
    { x, y }, // Mouse position
    points, // Pattern points
    pointActiveSize // Point active diameter
) => {
    for (let i = 0; i < points.length; i += 1) {
        if (
            x > points[i].x &&
            x < points[i].x + pointActiveSize &&
            y > points[i].y &&
            y < points[i].y + pointActiveSize
        ) return i;
    }
    return -1;
};

export const getConnectorPoint = (
    p,
    pointActiveSize,
    connectorThickness
) => ({
    x: p.x + Math.floor(pointActiveSize / 2),
    y: p.y + Math.floor(pointActiveSize / 2) - Math.floor(connectorThickness / 2)
});

export const exclusiveRange = (rawStart, stop) => {
    if (rawStart === stop) return [];
    const start = rawStart > stop ? rawStart - 1 : rawStart + 1;
    const step = start > stop ? -1 : 1;
    return Array.from({ length: Math.abs(start - stop) })
        .map((_, i) => start + i * step);
}

export const getPointsInTheMiddle = (index1, index2, size) => {
    const x1 = index1 % size;
    const x2 = index2 % size;

    const y1 = Math.floor(index1 / size);
    const y2 = Math.floor(index2 / size);
    const deltaX = Math.abs(x1 - x2);
    const deltaY = Math.abs(y1 - y2);

    if (y1 === y2) { // Horizontal
        return exclusiveRange(size * y1 + x1, size * y2 + x2);
    } else if (x1 === x2) { // Vertical
        return exclusiveRange(y1, y2).map(x => x * size + x1);
    } else if (deltaX === deltaY) { // Diagonal
        const m = x1 < x2 ? 1 : -1;
        return exclusiveRange(y1, y2).map((x, i) => x * size + x1 + ((i + 1) * m));
    }
    return [];
};


// --- VISCAT-359: camera-availability detection ------------------------------
// The candidate QR-scan page can only start once the browser both supports the
// app bundle and can hand the camera to getUserMedia. These pure, injectable
// helpers classify why the camera cannot start so URLInput can show friendly,
// cause-specific guidance instead of a blank/red-error dead end. There is no
// manual code-entry fallback by design.

// Synchronous pre-flight outcomes (known before we ever call getUserMedia).
export const CameraCapability = {
    SUPPORTED: 'SUPPORTED',
    BROWSER_UNSUPPORTED: 'BROWSER_UNSUPPORTED',
    INSECURE_CONTEXT: 'INSECURE_CONTEXT',
    CAMERA_API_ABSENT: 'CAMERA_API_ABSENT',
};

// Reasons a getUserMedia / html5-qrcode start attempt can throw.
export const CameraError = {
    PERMISSION_DENIED: 'PERMISSION_DENIED',
    NO_CAMERA: 'NO_CAMERA',
    CAMERA_IN_USE: 'CAMERA_IN_USE',
    UNKNOWN: 'UNKNOWN',
};

// Feature sentinel for the documented minimum-supported-browser floor
// (iPad/iOS Safari 15.4, macOS Safari 15.4, Chrome 108, Edge 108).
// structuredClone is baseline in Safari 15.4 (exact match to the floor) and
// Chrome 98 — see the Supported Browser Matrix (Confluence 263847938) and the
// pre-paint gate in public/index.html, which MUST use the same sentinel.
export const isSupportedBrowser = (g = window) => typeof g.structuredClone === 'function';

// Decide, synchronously, whether it is even worth calling getUserMedia.
// navigator/location are injectable so this unit-tests without a real browser.
export const checkCameraPreflight = ({
    navigator = window.navigator,
    location = window.location,
} = {}) => {
    if (!isSupportedBrowser()) return { status: CameraCapability.BROWSER_UNSUPPORTED };

    const hostname = location?.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    const isHTTPS = location?.protocol === 'https:';
    const isIOS = /iPad|iPhone|iPod/.test(navigator?.userAgent || '');

    // iOS/iPadOS only exposes the camera over a secure context.
    if (isIOS && !isHTTPS && !isLocalhost) return { status: CameraCapability.INSECURE_CONTEXT };

    if (!navigator?.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        return { status: CameraCapability.CAMERA_API_ABSENT };
    }

    return { status: CameraCapability.SUPPORTED };
};

// Map a thrown getUserMedia / html5-qrcode error to a stable reason, and report
// whether we are running as an installed (standalone/PWA) app so the permission
// copy can give the right recovery steps.
export const classifyCameraError = (err, {
    navigator = window.navigator,
    window: win = window,
} = {}) => {
    const name = err && err.name;
    const isStandalone =
        (typeof win?.matchMedia === 'function' &&
            win.matchMedia('(display-mode: standalone)').matches) ||
        Boolean(navigator && navigator.standalone) ||
        false;

    let reason = CameraError.UNKNOWN;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        reason = CameraError.PERMISSION_DENIED;
    } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        reason = CameraError.NO_CAMERA;
    } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        reason = CameraError.CAMERA_IN_USE;
    }

    return { reason, isStandalone };
};

// build the regEx pattern once
const reValidStatsPattern = /^(start|finger(Down|Up)|submit|undo|restart|dot-\d\d?)$/;
const reValidLevelPattern = /^(Practice|Direct Copy|Vertical Flip|Horizontal Flip|Survey)$/;

export default class TestResults {
    constructor(testLevel) {
        //TODO: we are probably going to have many page names in the future 
        // we may need to delete validation
        if (!reValidLevelPattern.test(testLevel)) {
            throw new Error(`"${testLevel}" is not a valide level.`);
        }
        this.level = testLevel;
        this.score = 0;
        this.countOfDemoPlays = 0;
        this.countOfAudioMutes = 0;
        this.testStatsData = [];
    }

    setScore(score) {
        if (score < 0 || score > 2) {
            throw new Error(`"${score}" is not a valide score. Must be between 0-2.`);
        }
        this.score = score;
    }

    incAudio() {
        this.countOfAudioMutes++;
    }

    incDemoPlays() {
        this.countOfDemoPlays++;
    }

    countOfStats() {
        return this.testStatsData.length;
    }

    addState(statName) {
        if (!reValidStatsPattern.test(statName)) {
            throw new Error(`"${statName}" is not a valide stat.`);
        }

        // Record when this stat was generated.
        const d = new Date();

        // Zero-pad date/time components to match fixed-width timestamp formatting.
        const pad2 = (n) => ('0' + n).slice(-2);
        const pad3 = (n) => ('00' + n).slice(-3);

        // Build the timestamp from local time components
        // so the datetime and UTC offset remain consistent.
        const year = d.getFullYear();
        const month = pad2(d.getMonth() + 1); // Months are zero-based.
        const day = pad2(d.getDate());
        const hours = pad2(d.getHours());
        const minutes = pad2(d.getMinutes());
        const seconds = pad2(d.getSeconds());
        const milliseconds = pad3(d.getMilliseconds());

        // JS timezone offsets use the opposite sign, so convert to ISO format (e.g. "+10:00").
        const offsetTotalMin = -d.getTimezoneOffset();
        const sign = offsetTotalMin >= 0 ? '+' : '-';
        const absMin = Math.abs(offsetTotalMin);
        const offH = pad2(Math.floor(absMin / 60));
        const offM = pad2(absMin % 60);

        // Format: yyyy-mm-ddTHH:mm:ss.sss±HH:mm
        // Example: "2024-06-27T16:30:45.123+02:00" (UTC: 14:30:45)
        const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offH}:${offM}`;

        this.testStatsData.push({
            "statName": statName,
            "statDateTime": formattedDateTime
        });
    }

}
